import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import styles from "./studentattendence.module.css";
import CustomMRTTable from "../../../components/CustomMRTTable/CustomMRTTable";
import { FaFemale, FaMale } from "react-icons/fa";
import { FaArrowLeft } from "react-icons/fa6";
import AxiosInstance, { TokenService } from "../../../services/Api";
import { CustomButton } from "../../../components";

const StudentAttendanceListView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);
  const { classId } = location.state || {};
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  useEffect(() => {
    fetchClassAttendanceSummary();
  }, [pagination.pageIndex, pagination.pageSize, apiSearch, classId]);

  const fetchClassAttendanceSummary = async () => {
    setLoading(true);
    try {
      // Get today's date in YYYY-MM-DD format
      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0];

      const response = await AxiosInstance.post("/attendance/section-wise", {
        school_id: parseInt(school_id),
        class_id: classId,
        date: formattedDate,
        search: apiSearch || "",
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      });

      if (response?.data?.status && response?.data?.data) {
        const responseData = response.data.data;
        setData(responseData.section || []);
        setRowCount(
          responseData.total_count || responseData.section?.length || 0,
        );
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching class attendance summary", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "section",
        header: translations?.labels?.section || "Section",
        size: 120,
        Cell: ({ row }) => (
          <div
            style={{ textAlign: "center", fontWeight: 600, fontSize: "16px" }}
          >
            {row.original.section_name}
          </div>
        ),
        enableSorting: false,
      },
      {
        id: "enrolled",
        header: translations?.labels?.enrolled || "Enrolled",
        size: 200,
        Cell: ({ row }) => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <div style={{ fontWeight: 600, fontSize: "16px" }}>
              {row.original.section_total_count}
            </div>
            <div
              style={{
                display: "flex",
                gap: "20px",
                fontSize: "14px",
                fontWeight: 400,
              }}
            >
              <span className={styles.maleStat}>
                <FaMale size={14} />{" "}
                {parseInt(row.original.section_male_count) || 0}
              </span>
              <span className={styles.femaleStat}>
                <FaFemale size={14} />{" "}
                {parseInt(row.original.section_female_count) || 0}
              </span>
            </div>
          </div>
        ),
        enableSorting: false,
      },
      {
        id: "present",
        header: translations?.labels?.present || "Present",
        size: 200,
        Cell: ({ row }) => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <div style={{ fontWeight: 600, fontSize: "16px" }}>
              {parseInt(row.original.present_count) || 0}
            </div>
            <div
              style={{
                display: "flex",
                gap: "20px",
                fontSize: "14px",
                fontWeight: 400,
              }}
            >
              <span className={styles.maleStat}>
                <FaMale size={14} />{" "}
                {parseInt(row.original.male_present_count) || 0}
              </span>
              <span className={styles.femaleStat}>
                <FaFemale size={14} />{" "}
                {parseInt(row.original.female_present_count) || 0}
              </span>
            </div>
          </div>
        ),
        enableSorting: false,
      },
      {
        id: "absent",
        header: translations?.labels?.absent || "Absent",
        size: 200,
        Cell: ({ row }) => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <div style={{ fontWeight: 600, fontSize: "16px" }}>
              {parseInt(row.original.absent_count) || 0}
            </div>
            <div
              style={{
                display: "flex",
                gap: "20px",
                fontSize: "14px",
                fontWeight: 400,
              }}
            >
              <span className={styles.maleStat}>
                <FaMale size={14} />{" "}
                {parseInt(row.original.male_absent_count) || 0}
              </span>
              <span className={styles.femaleStat}>
                <FaFemale size={14} />{" "}
                {parseInt(row.original.female_absent_count) || 0}
              </span>
            </div>
          </div>
        ),
        enableSorting: false,
      },
      {
        id: "leave",
        header: translations?.labels?.leave || "Leave",
        size: 200,
        Cell: ({ row }) => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <div style={{ fontWeight: 600, fontSize: "16px" }}>
              {parseInt(row.original.leave_count) || 0}
            </div>
            <div
              style={{
                display: "flex",
                gap: "20px",
                fontSize: "14px",
                fontWeight: 400,
              }}
            >
              <span className={styles.maleStat}>
                <FaMale size={14} />{" "}
                {parseInt(row.original.male_leave_count) || 0}
              </span>
              <span className={styles.femaleStat}>
                <FaFemale size={14} />{" "}
                {parseInt(row.original.female_leave_count) || 0}
              </span>
            </div>
          </div>
        ),
        enableSorting: false,
      },
    ],
    [translations],
  );

  return (
    <section id="content" className={styles.listView}>
      <div className="container-fluid py-4">
        {/* Breadcrumbs and Back Button */}
        <div className="row mb-4 align-items-center">
          <div className="col-md-6"></div>
          <div className="col-md-6 text-end">
            <CustomButton
              fullWidth={false}
              variant="contained"
              className={styles.backButton}
              onClick={() => navigate("/user/Student-Attendance-Dashboard")}
            >
              <FaArrowLeft className="me-2" size={14} />
              {translations?.labels?.back || "Back"}
            </CustomButton>
          </div>
        </div>

        {/* Table Card */}
        <div className={styles.tableCard}>
          <CustomMRTTable
            columns={columns}
            data={data}
            loading={loading}
            pagination={pagination}
            rowCount={rowCount}
            onPaginationChange={setPagination}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            onSearchEnter={(value) => {
              setApiSearch(value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            enableSearch={true}
            enableFiltering={false}
            placeholder={
              translations?.labels?.searchPlaceholder || "Search ..."
            }
            muiTableProps={{
              sx: {
                border: "1px solid #e0e0e0",
                borderRadius: "12px",
                overflow: "hidden",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: "#ffffff",
                color: "#1565c0",
                borderBottom: "2px solid #1565c0",
                fontWeight: 600,
                textTransform: "uppercase",
                fontSize: "13.5px",
                textAlign: "center !important",
              },
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default StudentAttendanceListView;
